import React, { useState, useEffect } from 'react';
import { Card, Input, Select, Textarea, FormGrid, Btn, Modal, useToast, DatePicker, Spinner, SlowRequestHint, ReturnDestinationNote } from '../UI';
import * as api from '../../api';
import { belongsInMyDrafts, errorText, runChecks, missingFieldsMessage, isAwaitingCorrection, correctionTarget, OTHER_OPTION, withOtherOption, otherAwareId, otherAwareText, otherAwareValue, bypassesDepartmentReview} from '../../utils';
import MyInbox from './MyInbox';
import MyRequisitions from './MyRequisitions';
import RequestedForField from './RequestedForField';
import RequisitionLineItemsTable, { lineItemFromHistory, mapLineItemForApi, lineItemFromApi, computeRequisitionTotals } from './RequisitionLineItemsTable';
import PurchaseHistoryModal from './PurchaseHistoryModal';

const fmt = (v) => (v != null && !isNaN(v) ? parseFloat(v).toLocaleString('en-NG', { minimumFractionDigits: 2 }) : '0.00');

// Line-item grid styles now live in RequisitionLineItemsTable — the shared
// grid every non-HR requisition form renders.

const emptyItem = () => ({
  item_id: null,
  req_date: new Date().toISOString().split('T')[0],
  material_id: '',
  description: '',
  specification: '',
  vendor_id: '',
  project_id: '',
  boq_item_id: '',
  boq_component_id: '',
  expense_category_id: '',
  brand: '',
  unit_of_measure: 'pcs',
  quantity: 1,
  estimated_unit_rate: 0,
  vat_percentage: 0,
  cost_centre_id: '',
  account_ledger_id: '',
  ledger_id: '',
  cost_object_type_id: '',
  category_asset_type_id: '',
  additional_info: '',
  priority_status: 'normal',
  remarks: '',
  last_date: null,
  last_qty: null,
  last_rate: null,
  last_total: null,
  last_vendor_id: '',
  last_vendor_name: '',
});

// ─── Select From Previous Purchases modal ───────────────────────────────────

// ─── Main component ──────────────────────────────────────────────────────────
export default function AdminCorporateServicesRequisition({ onBack, onSaveSuccess, editPrId, department, subView, onViewChange }) {
  const todayDate = new Date().toISOString().split('T')[0];
  const { show: showToast } = useToast();

  // 'form' | 'review' | 'drafts'
  //
  // Controlled when the caller passes subView, uncontrolled otherwise. Both are
  // aliased to the same names so every reference below is untouched — there is
  // no second code path to keep in step, and callers that pass nothing behave
  // exactly as they did before.
  const [viewState, setViewState] = useState('form');
  const view = subView || viewState;
  const setView = onViewChange || setViewState;
  const [reviewCount, setReviewCount] = useState(0);
  const [draftCount, setDraftCount] = useState(0);
  // True while My Drafts has a requisition open for correction — see the
  // drafts view below.
  const [editingDraft, setEditingDraft] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Populated when editing an existing PR, so a corrected RETURNED request can
  // be resubmitted straight back to the step that returned it. Without these
  // this form could save the corrections but never send them onward — the
  // edits looked saved and simply never moved.
  const [editWorkflowInstanceId, setEditWorkflowInstanceId] = useState(null);
  // Where Save & Resubmit will send this request. Read off the loaded
  // request rather than fetched, and shown in the confirm dialog so this
  // route names its destination like the inbox and details page do.
  const [resubmitTarget, setResubmitTarget] = useState('');
  const [resubmitComment, setResubmitComment] = useState('');
  const [editPrStatus, setEditPrStatus] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  // Their requisitions skip Department Head Review, so the button must not
  // promise a review that will not happen, and afterwards they belong in My
  // Requisitions rather than a department queue they never enter.
  const skipsDepartmentReview = bypassesDepartmentReview(currentUser);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseRequestTypes, setPurchaseRequestTypes] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  const [accountLedgers, setAccountLedgers] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expenseSubCategories, setExpenseSubCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [boqDataByProject, setBoqDataByProject] = useState({});
  const [errors, setErrors] = useState({});
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const [formData, setFormData] = useState({
    pr_number: '',
    title: '',
    description: '',
    requested_for: '',
    requested_department_id: department?.id || department?.department_id || '',
    purchase_request_type_id: '',
    purchase_request_type_other: '',
    priority_status: 'normal',
    required_date: todayDate,
    expected_due_date: todayDate,
    remarks: '',
    adjustments_amount: 0,
    adjustment_reason: '',
    require_vendor: false,
  });

  const [items, setItems] = useState([emptyItem()]);

  const [confirmModal, setConfirmModal] = useState(null);
  const pendingStatusRef = React.useRef(null);
  const [showApproverModal, setShowApproverModal] = useState(false);
  const [firstStepInfo, setFirstStepInfo] = useState(null);
  const [approvers, setApprovers] = useState([]);
  const [selectedApproverId, setSelectedApproverId] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedPrRef, setSubmittedPrRef] = useState('');

  const fetchReviewCount = async (deptId) => {
    if (!deptId) return;
    try {
      const res = await api.getDepartmentInbox(deptId, 'Department Head Review');
      const list = Array.isArray(res) ? res : (res?.data || []);
      setReviewCount(list.length);
    } catch (e) {
      // non-fatal — badge just stays at 0
    }
  };

  const fetchDraftCount = async (deptId) => {
    if (!deptId) return;
    try {
      const res = await api.listMyPRs();
      const list = Array.isArray(res) ? res : (res?.data || []);
      // Same predicate the My Drafts list uses, so the badge and the tab can
      // never disagree — a department-head bounce sits in drafts and must be
      // counted there too.
      const count = list.filter((pr) =>
        belongsInMyDrafts(pr) &&
        String(pr.requested_department_id || '') === String(deptId)
      ).length;
      setDraftCount(count);
    } catch (e) {
      // non-fatal — badge just stays at 0
    }
  };

  // After a brand-new (non-edit) draft save or submission, we land the user
  // on the My Drafts / Manager Review pill instead of navigating away — so
  // the form underneath needs to be blanked out itself, otherwise reopening
  // "New Requisition" would show the just-saved values instead of a blank form.
  const resetFormForNewEntry = async () => {
    const deptId = department?.id || department?.department_id || '';

    setFormData({
      pr_number: '',
      title: '',
      description: '',
      requested_for: '',
      requested_department_id: deptId,
      purchase_request_type_id: '',
    purchase_request_type_other: '',
      priority_status: 'normal',
      required_date: todayDate,
      expected_due_date: todayDate,
      remarks: '',
    adjustments_amount: 0,
      adjustment_reason: '',
    });
    setItems([emptyItem()]);
    setErrors({});
  };

  const handleSuccessModalClose = async () => {
    setShowSuccessModal(false);
    // A bypassing requester never enters this department's review queue, so
    // the pending-review view would be empty and their new requisition
    // nowhere in sight. Send them to My Requisitions, where it actually is.
    if (editPrId || skipsDepartmentReview) {
      onSaveSuccess?.();
    } else {
      const deptId = department?.id || department?.department_id;
      await resetFormForNewEntry();
      fetchReviewCount(deptId);
      setView('review');
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        api.clearApiCache();
        const [deptRes, projRes, matRes, suppRes, typeRes, ledgerRes, ccRes, alRes, ecRes, escRes, meRes, usersRes] = await Promise.all([
          api.listDepartments(0, 100, true).catch(() => ({ departments: [] })),
          api.listProjects().catch(() => ({ data: [] })),
          api.listMaterials(0, undefined, '', department?.id || department?.department_id || '').catch(() => ({ data: [] })),
          api.listSuppliers().catch(() => ({ data: [] })),
          api.listPurchaseRequestTypes(true).catch(() => ({ data: [] })),
          api.listLedgers(true).catch(() => ({ ledgers: [] })),
          api.listCostCentres(true).catch(() => ({ cost_centres: [] })),
          api.listAccountLedgers().catch(() => ({ data: [] })),
          api.listExpenseCategories().catch(() => ({ data: [] })),
          api.listExpenseSubCategories().catch(() => ([])),
          api.getMe().catch(() => null),
          api.listUsers(0, 500).catch(() => []),
        ]);
        setDepartments(Array.isArray(deptRes) ? deptRes : (deptRes.departments || deptRes.data || []));
        setProjects(Array.isArray(projRes) ? projRes : (projRes.data || []));
        setMaterials(Array.isArray(matRes) ? matRes : (matRes.data || []));
        setSuppliers(Array.isArray(suppRes) ? suppRes : (suppRes.data || suppRes.items || []));
        setPurchaseRequestTypes(Array.isArray(typeRes) ? typeRes : (typeRes.types || typeRes.data || []));
        setLedgers(Array.isArray(ledgerRes) ? ledgerRes : (ledgerRes.ledgers || ledgerRes.data || []));
        setCostCentres(Array.isArray(ccRes) ? ccRes : (ccRes.cost_centres || ccRes.data || []));
        setAccountLedgers(Array.isArray(alRes) ? alRes : (alRes.data || []));
        setExpenseCategories(Array.isArray(ecRes) ? ecRes : (ecRes.data || []));
        setExpenseSubCategories(Array.isArray(escRes) ? escRes : (escRes?.data || []));
        setCurrentUser(meRes);
        setUsers(Array.isArray(usersRes) ? usersRes : (usersRes?.data || []));
        if (!editPrId) {
          fetchReviewCount(department?.id || department?.department_id);
          fetchDraftCount(department?.id || department?.department_id);
        }

        if (editPrId) {
          const prRes = await api.getPR(editPrId);
          const pr = prRes?.data || prRes;
          setEditWorkflowInstanceId(pr.workflow_instance_id || null);
          setResubmitTarget(isAwaitingCorrection(pr) ? correctionTarget(pr) : '');
          setEditPrStatus((pr.request_status || '').toLowerCase());
          fetchReviewCount(pr.requested_department_id);
          fetchDraftCount(pr.requested_department_id);
          setFormData({
            pr_number: pr.pr_number || '',
            title: pr.title || '',
            description: pr.description || '',
            requested_for: pr.requested_for || '',
            requested_department_id: pr.requested_department_id || '',
            purchase_request_type_id: otherAwareValue(
              pr.purchase_request_type_id, pr.purchase_request_type_other),
            purchase_request_type_other: pr.purchase_request_type_other || '',
            priority_status: pr.priority_status || 'normal',
            required_date: pr.required_date || todayDate,
            expected_due_date: pr.expected_due_date || todayDate,
            remarks: pr.remarks || '',
            adjustments_amount: pr.adjustments_amount || 0,
            adjustment_reason: pr.adjustment_reason || '',
            require_vendor: pr.require_vendor || false,
          });
          // Shared loader, matching the shared mapper above. The local copy
          // that was here did not know about the "Others" pickers, so a saved
          // free-text material came back as a blank selection.
          const loadedItems = (pr.items && pr.items.length > 0)
            ? pr.items.map(lineItemFromApi)
            : [emptyItem()];
          setItems(loadedItems);
        } else {

        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [editPrId]);

  // Re-check the My Drafts count whenever the user leaves that tab (e.g.
  // after submitting or deleting a draft there), so the badge doesn't go stale.
  useEffect(() => {
    if (view !== 'drafts') {
      fetchDraftCount(formData.requested_department_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // ─── item helpers ─────────────────────────────────────────────────────────
  const updateItem = async (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

    if (patch.project_id !== undefined && patch.project_id) {
      if (!boqDataByProject[patch.project_id]) {
        try {
          const boqRes = await api.listBOQ(patch.project_id);
          const boqItems = Array.isArray(boqRes) ? boqRes : (boqRes?.data || boqRes?.items || []);
          setBoqDataByProject((prev) => ({ ...prev, [patch.project_id]: boqItems }));
        } catch (e) {
          console.error('Failed to load project BOQs', e);
        }
      }
      setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, boq_item_id: '', boq_component_id: '' } : it)));
    }
  };
  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  const addHistoricalItems = (historicalItems) => {
    const newLines = historicalItems.map((h) => lineItemFromHistory(h, todayDate, { requireVendor: formData.require_vendor }));
    setItems((prev) => {
      const withoutBlank = prev.filter((it) => it.description || it.material_id);
      return [...withoutBlank, ...newLines];
    });
    setShowHistoryModal(false);
  };

  // ─── totals ───────────────────────────────────────────────────────────────
  // VAT is carried by the individual lines, so the requisition's tax is the
  // sum of theirs. Same arithmetic the server runs on what it is sent.
  const { gross: grossAmount, vat: vatAmount, total: totalRequisitionValue } =
    computeRequisitionTotals(items, formData.adjustments_amount);
  const historicalRefCount = new Set(items.filter((it) => it.last_date).map((it) => it.last_vendor_id)).size;

  // ─── save / submit ────────────────────────────────────────────────────────
  // The shared mapper, not a local copy. The copy that used to live here
  // predated the "Others" pickers and sent the sentinel straight through as
  // material_id — the server rejected it as an invalid UUID, which is what
  // broke resubmitting a corrected requisition. One mapper means this form
  // and New Requisition cannot drift apart again.
  const buildMappedItems = () => items.map(it => mapLineItemForApi(it, { requireVendor: formData.require_vendor }));

  const validate = () => {
    // Every row is checked. Rows only ever exist because the user clicked
    // "Add Line" — nothing appends a spare — so a blank row is an unfinished
    // line, not something to skip. Removing a row they no longer want is the
    // delete button's job, and that always leaves at least one behind.
    const checks = [
      { key: 'title', label: 'Title / Purpose', ok: !!formData.title },
      { key: 'requested_department_id', label: 'Department', ok: !!formData.requested_department_id },
    ];
    if (items.length === 0) checks.push({ key: 'items', label: 'At least one line item', ok: false });
    items.forEach((item, idx) => {
      const line = `Line ${idx + 1}`;
      // Both columns are NOT NULL on purchase_request_items, so a line missing
      // either is rejected by the server anyway — flagging it here turns a bare
      // 422 into a red border on the field at fault.
      checks.push({ key: `project_id_${idx}`, label: `${line} project`, ok: !!item.project_id });
      checks.push({ key: `material_id_${idx}`, label: `${line} material`, ok: !!item.material_id });
    });

    const { errors: newErrors, valid, missing } = runChecks(checks);
    setErrors(newErrors);
    if (!valid) showToast(missingFieldsMessage(missing), 'error');
    return valid;
  };

  const handleSaveClick = (status) => {
    if (!validate()) return;
    if (status === 'SUBMITTED') {
      beginSubmitFlow();
    } else {
      setConfirmModal(status);
    }
  };

  const beginSubmitFlow = async () => {
    setSaving(true);
    try {
      const stepRes = await api.getEligibleApprovers('PR_APPROVAL', {
        requested_department_id: formData.requested_department_id || null,
      });
      const data = stepRes?.data || stepRes;
      setFirstStepInfo(data);
      if (data?.approver_mode === 'specific_user') {
        const eligible = data.eligible_users || [];
        setApprovers(eligible);
        setSelectedApproverId(eligible.length > 0 ? (eligible[0].user_id || eligible[0].id) : '');
      } else {
        setApprovers([]);
        setSelectedApproverId('');
      }
      pendingStatusRef.current = 'SUBMITTED';
      setShowApproverModal(true);
    } catch (err) {
      showToast(`Could not start the submission: ${errorText(err)}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const executeAction = async (overrideApproverId = null, intent = null) => {
    if (confirmModal === 'DISCARD') {
      api.clearApiCache();
      onBack?.();
      setConfirmModal(null);
      return;
    }

    // The caller says what it is doing. This used to be inferred as
    // `confirmModal || pendingStatusRef.current`, which let a stale
    // confirmModal outrank the submit intent: a failed save left confirmModal
    // set to 'DRAFT' (the catch block never cleared it), so the NEXT submit
    // resolved to 'DRAFT' and was silently saved as a draft with no workflow
    // started. The explicit argument wins; the old sources remain only as a
    // fallback for callers that have not been given one.
    const status = intent || confirmModal || pendingStatusRef.current;
    setSaving(true);
    try {
      const isSubmitting = status === 'SUBMITTED';
      // `''` is not an override, it is "nothing chosen yet". The old test was
      // `overrideApproverId !== null`, and '' passes that — so an empty string
      // reached the API, failed UUID parsing, and (on an existing draft) left
      // the edits saved with the submission silently lost.
      const rawApprover = overrideApproverId || (
        firstStepInfo?.approver_mode === 'specific_user' ? selectedApproverId : undefined
      );
      const approverId = rawApprover || undefined;

      // Fail here rather than half-way: nothing is written until we know the
      // submission can actually go through.
      if (isSubmitting && firstStepInfo?.approver_mode === 'specific_user' && !approverId) {
        showToast('Select the first approver before submitting.', 'error');
        return;
      }
      const mappedItems = buildMappedItems();

      let res;
      if (editPrId) {
        res = await api.updatePR(editPrId, {
          title: formData.title,
          description: formData.description || null,
          priority_status: formData.priority_status || 'normal',
          required_date: formData.required_date || null,
          expected_due_date: formData.expected_due_date || null,
          // '' clears it server-side; a name sets it.
          requested_for: (formData.requested_for || '').trim(),
          requested_department_id: formData.requested_department_id || null,
          purchase_request_type_id: otherAwareId(formData.purchase_request_type_id),
          purchase_request_type_other: otherAwareText(
            formData.purchase_request_type_id, formData.purchase_request_type_other),
          remarks: formData.remarks || null,
          total_estimated_cost: totalRequisitionValue,
          vat_amount: Number(vatAmount.toFixed(2)),
          adjustments_amount: formData.adjustments_amount || null,
          adjustment_reason: formData.adjustment_reason || null,
          require_vendor: formData.require_vendor,
          items: mappedItems,
        });
        // updatePR only persists the edits — unlike createPR it carries no
        // submit_for_approval flag. Without this, hitting Submit on a saved
        // draft quietly re-saved it and left it sitting in My Drafts.
        if (status === 'RESUBMIT' && editWorkflowInstanceId) {
          // Sends the corrected request back to whoever returned it. The
          // server decides where that is — for a request returned to an
          // approver it is handled as an approve-forward.
          await api.resubmitWorkflow(editWorkflowInstanceId, resubmitComment.trim() || null);
        } else if (isSubmitting) {
          await api.submitWorkflow({
            entity_type: 'purchase_request',
            entity_id: editPrId,
            definition_code: 'PR_APPROVAL',
            first_approver_id: approverId,
          });
        }
      } else {
        const payload = {
          pr_number: formData.pr_number || undefined,
          title: formData.title,
          description: formData.description,
          // '' clears it server-side; a name sets it.
          requested_for: (formData.requested_for || '').trim(),
          requested_department_id: formData.requested_department_id || null,
          purchase_request_type_id: otherAwareId(formData.purchase_request_type_id),
          purchase_request_type_other: otherAwareText(
            formData.purchase_request_type_id, formData.purchase_request_type_other),
          priority_status: formData.priority_status || 'normal',
          required_date: formData.required_date || null,
          expected_due_date: formData.expected_due_date || null,
          remarks: formData.remarks || null,
          items: mappedItems,
          total_estimated_cost: totalRequisitionValue,
          vat_amount: Number(vatAmount.toFixed(2)),
          adjustments_amount: formData.adjustments_amount || null,
          adjustment_reason: formData.adjustment_reason || null,
          require_vendor: formData.require_vendor,
          submit_for_approval: isSubmitting,
          first_approver_id: isSubmitting ? approverId : undefined,
        };
        res = await api.createPR(payload);
      }

      api.clearApiCache();
      if (isSubmitting) {
        setSubmittedPrRef(res?.pr_number || formData.pr_number || '');
        setConfirmModal(null);
        setShowApproverModal(false);
        setShowSuccessModal(true);
      } else {
        showToast(
          editPrId
            ? 'Requisition updated!'
            : `Requisition ${res?.pr_number || ''} saved to Draft!`.replace('  ', ' '),
          'success',
        );
        setConfirmModal(null);
        setShowApproverModal(false);
        if (editPrId) {
          onSaveSuccess?.();
        } else {
          const deptId = department?.id || department?.department_id;
          await resetFormForNewEntry();
          fetchDraftCount(deptId);
          setView('drafts');
        }
      }
    } catch (e) {
      console.error(e);
      showToast(`Could not save this requisition: ${errorText(e)}`, 'error');
      // Cleared on failure too. Left set, it became the `status` of whatever
      // the user tried next.
      setConfirmModal(null);
    } finally {
      setSaving(false);
      pendingStatusRef.current = null;
    }
  };

  const backButton = (
    <button
      type="button"
      onClick={() => { api.clearApiCache(); onBack?.(); }}
      style={{
        padding: '8px 16px', borderRadius: 8, border: '1px solid black', background: '#0b318bff',
        color: '#ffffff', fontWeight: 900, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
      }}
    >
      ← <span style={{ fontWeight: 900 }}>{editPrId ? 'Back to My Requisitions' : 'Back to Department Hub'}</span>
    </button>
  );

  // Suppressed while editing one requisition. In that mode this component is
  // rendered *inside* My Drafts, which already sits under the department hub's
  // own tab row — so drawing them again duplicated the whole nav. They would
  // also be a trap: switching tab mid-edit silently abandons the correction.
  // Closing a correction can change what is in drafts — a resubmitted
  // requisition leaves them — so the badge is recomputed rather than left
  // showing a stale count.
  useEffect(() => {
    if (!editingDraft) fetchDraftCount(formData.requested_department_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingDraft]);

  const pillRow = !editPrId && (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <button onClick={() => setView('form')} style={pillStyle(view === 'form')}>New Requisition</button>
      <button onClick={() => setView('review')} style={pillStyle(view === 'review')}>Admin Manager Review {reviewCount > 0 && <Badge count={reviewCount} />}</button>
      <button onClick={() => setView('drafts')} style={pillStyle(view === 'drafts')}>My Drafts {draftCount > 0 && <Badge count={draftCount} />}</button>
    </div>
  );

  if (view === 'review') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Hidden while a request is open for correction — that editor brings
            its own "Back to My Requisitions". */}
        {!editingDraft && backButton}
        {!editingDraft && pillRow}
        <MyInbox onEditingChange={setEditingDraft} stepNameFilter="Department Head Review" title="Admin Manager Review" departmentId={formData.requested_department_id} onUnseenCountChange={setReviewCount} />
      </div>
    );
  }

  if (view === 'drafts') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Hidden while a returned requisition is open for correction: that
            editor brings its own "Back to My Requisitions", and the hub's back
            bar and tabs would sit above it as a second, competing way out. */}
        {!editingDraft && backButton}
        {!editingDraft && pillRow}
        <MyRequisitions
          title="My Drafts"
          departmentId={formData.requested_department_id}
          draftsOnly
          onEditingChange={setEditingDraft}
        />
      </div>
    );
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading requisition form...</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
        {backButton}
        <div style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>
          Selected Form Type: <span style={{ color: '#0b318bff', fontWeight: 700 }}>Administration &amp; Corporate Services Requisition</span>
        </div>
      </div>

      {pillRow}

      <Card style={{ padding: 24, border: '1px solid black' }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: '#0b318bff' }}>Requisition Header</h3>
        <FormGrid cols={4}>
          {/* Empty until the server issues one — see NewRequisition. */}
          <div>
            <Input label="Requisition Ref ID" value={formData.pr_number} placeholder="Will be generated on submission" disabled style={{ fontWeight: 'bold', color: '#0b318bff', border: '1px solid black' }} />
            {!formData.pr_number && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}>
                Generated automatically when you submit or save.
              </div>
            )}
          </div>
          <Input
            label="Title / Purpose"
            placeholder="e.g. Office stationery for Q3"
            value={formData.title}
            style={{ border: errors.title && !formData.title ? '2px solid #ef4444' : undefined }}
            onChange={(v) => setFormData({ ...formData, title: v })}
          />
          <Input label="Requester" disabled value={currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : ''} style={{ fontWeight: 'bold', color: '#0b318bff', border: '1px solid black' }} />
          <RequestedForField
            value={formData.requested_for}
            onChange={(v) => setFormData({ ...formData, requested_for: v })}
          />
          <Select
            label="Department"
            required
            options={departments.map((d) => ({ value: d.id || d.department_id, label: d.name }))}
            value={formData.requested_department_id}
            onChange={(v) => setFormData({ ...formData, requested_department_id: v })}
            style={{ border: errors.requested_department_id && !formData.requested_department_id ? '2px solid #ef4444' : undefined }}
          />

          <Select
            label="Request Type"
            options={withOtherOption(purchaseRequestTypes.map((t) => ({ value: t.id || t.type_id, label: t.name })))}
            value={formData.purchase_request_type_id}
            onChange={(v) => setFormData({ ...formData, purchase_request_type_id: v })}
          />
          {formData.purchase_request_type_id === OTHER_OPTION && (
            <Input
              label="Specify Request Type"
              value={formData.purchase_request_type_other}
              onChange={(v) => setFormData({ ...formData, purchase_request_type_other: v })}
              placeholder="Type the request type"
              required
            />
          )}
          <Select
            label="Priority"
            options={[
              { value: 'low', label: 'Low' },
              { value: 'normal', label: 'Normal' },
              { value: 'high', label: 'High' },
              { value: 'urgent', label: 'Urgent' },
              { value: 'critical', label: 'Critical' },
            ]}
            value={formData.priority_status}
            onChange={(v) => setFormData({ ...formData, priority_status: v })}
          />
          <DatePicker label="Required Date" value={formData.required_date} onChange={(v) => setFormData({ ...formData, required_date: v })} />
          <DatePicker label="Expected Due Date" value={formData.expected_due_date} onChange={(v) => setFormData({ ...formData, expected_due_date: v })} />

          {/* Forced to column 1 so the two text areas always start a fresh
              row and stay side by side. The single-width fields above them
              are an odd count, so left to flow, Purpose slid up beside
              Expected Due Date and pushed Remarks onto a row of its own. */}
          <div style={{ gridColumn: '1 / span 2' }}>
            <Textarea label="Purpose / Justification" rows={2} placeholder="Brief purpose or justification" value={formData.description} onChange={(v) => setFormData({ ...formData, description: v })} />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <Textarea label="Remarks" rows={2} placeholder="Any additional remarks..." value={formData.remarks} onChange={(v) => setFormData({ ...formData, remarks: v })} />
          </div>
        </FormGrid>
      </Card>

      <Card style={{ padding: 0, border: '1px solid black', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid black', background: '#f8fafc' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0b318bff', margin: 0 }}>Line Items</h3>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {items.length} item(s) · Total: ₦{fmt(grossAmount)} · {historicalRefCount} historical ref(s)
              </div>
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer', userSelect: 'none', fontSize: 12.5, fontWeight: 600, color: formData.require_vendor ? '#0b318b' : '#64748b' }}>
              <div
                onClick={() => {
                  const turningOff = formData.require_vendor;
                  if (turningOff) {
                    setItems(prev => prev.map(it => ({ ...it, vendor_id: '', vendor_other: '' })));
                  }
                  setFormData(prev => ({ ...prev, require_vendor: !prev.require_vendor }));
                }}
                style={{
                  width: 36, height: 20, borderRadius: 10, background: formData.require_vendor ? '#0b318b' : '#cbd5e1',
                  position: 'relative', transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0,
                }}
              >
                <div style={{
                  position: 'absolute', top: 2, left: formData.require_vendor ? 18 : 2,
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
              Require a Vendor?
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={() => setShowHistoryModal(true)} style={{ background: '#f97316', color: '#fff', border: 'none' }}>Select From Previous Purchases</Btn>
            <Btn onClick={addItem} style={{ background: '#0b318bff', color: '#fff', border: 'none' }}>Add Line</Btn>
          </div>
        </div>

        <RequisitionLineItemsTable
          items={items}
          updateItem={updateItem}
          removeItem={removeItem}
          errors={errors}
          materials={materials}
          suppliers={suppliers}
          projects={projects}
          boqDataByProject={boqDataByProject}
          expenseCategories={expenseCategories}
          expenseSubCategories={expenseSubCategories}
          costCentres={costCentres}
          accountLedgers={accountLedgers}
          ledgers={ledgers}
          users={users}
          requireVendor={formData.require_vendor}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', gap: 24, padding: 20, borderTop: '1px solid black' }}>
          <div style={{ minWidth: 220 }}>
            <Input label="Adjustments (₦)" type="number" value={formData.adjustments_amount} onChange={(v) => setFormData({ ...formData, adjustments_amount: v })} />
          </div>
          <div style={{ minWidth: 260 }}>
            <Input label="Adjustment Reason" value={formData.adjustment_reason} onChange={(v) => setFormData({ ...formData, adjustment_reason: v })} />
          </div>
          <div style={{ textAlign: 'right' }}>
            {/* Spelled out so an approver can see what the total is made of
                rather than having to add the lines back up. */}
            <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 4 }}>
              Sub-total ₦{fmt(grossAmount)} · VAT ₦{fmt(vatAmount)}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>TOTAL ESTIMATED COST</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#0b318bff' }}>₦{fmt(totalRequisitionValue)}</div>
          </div>
        </div>
      </Card>

      <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
        <button
          onClick={() => setConfirmModal('DISCARD')}
          disabled={saving}
          style={{
            background: '#ef4444', color: '#fff', border: 'none', padding: '12px 24px',
            borderRadius: 8, fontWeight: 900,
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1,
          }}
        >
          Cancel
        </button>
        <button
          onClick={() => handleSaveClick('DRAFT')}
          disabled={saving}
          style={{ background: '#eab308', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 8, fontWeight: 900, cursor: saving ? 'not-allowed' : 'pointer' }}
        >
          {editPrId ? 'Save Changes' : 'Save Draft'}
        </button>
        {editPrId && editPrStatus === 'returned' && (
          <button
            onClick={() => handleSaveClick('RESUBMIT')}
            disabled={saving}
            style={{
              background: '#16a34a', color: '#fff', border: 'none', padding: '12px 24px',
              borderRadius: 8, fontWeight: 900, cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8, opacity: saving ? 0.85 : 1,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
            {saving ? 'Resubmitting…' : 'Save & Resubmit'}
          </button>
        )}
        {!editPrId && (
          <button
            onClick={() => handleSaveClick('SUBMITTED')}
            disabled={saving}
            style={{
              background: '#16a34a', color: '#fff', border: 'none', padding: '12px 24px',
              borderRadius: 8, fontWeight: 900, cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8, opacity: saving ? 0.85 : 1,
            }}
          >
            {saving && <Spinner size={15} />}
            {saving ? 'Submitting…' : (skipsDepartmentReview ? 'Submit to Cost Control' : 'Submit to Administration Review')}
          </button>
        )}
      </div>

      {showHistoryModal && (
        <PurchaseHistoryModal onCancel={() => setShowHistoryModal(false)} onAdd={addHistoricalItems} />
      )}

      {/* Mirrors the inbox's Authorize Request dialog — same width, same
          layout, same wording — because it is the same action underneath:
          resubmit delegates to approve when a request was returned to its
          holder for correction. Two dialogs for one action is what made the
          three routes look like three different things. */}
      {confirmModal === 'RESUBMIT' && (
        <Modal title="Authorize Request" onClose={saving ? () => { } : () => setConfirmModal(null)}>
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 13, marginBottom: 16 }}>
              You are about to <strong>save and resubmit</strong> the purchase request
              <strong> {formData.pr_number}</strong>.
            </p>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                Comment
              </label>
              <Input
                value={resubmitComment}
                onChange={setResubmitComment}
                placeholder="Optional comment..."
                as="textarea"
                rows={3}
                disabled={saving}
              />
            </div>

            {resubmitTarget && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  Goes back to - {resubmitTarget}
                </label>
                <ReturnDestinationNote stepName={resubmitTarget} />
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Btn variant="outline" disabled={saving} onClick={() => setConfirmModal(null)}>Cancel</Btn>
            <Btn
              onClick={() => executeAction(null, confirmModal)}
              loading={saving}
              style={{ background: '#16a34a', color: '#fff', border: 'none' }}
            >
              {saving ? 'Resubmitting…' : 'Confirm Resubmit'}
            </Btn>
          </div>
        </Modal>
      )}

      {confirmModal && confirmModal !== 'RESUBMIT' && (
        <Modal title="Confirm Action" onClose={saving ? () => { } : () => setConfirmModal(null)} width={400}>
          <div style={{ padding: 24, textAlign: 'center' }}>
            <p style={{ fontSize: 15, color: '#475569', marginBottom: 24 }}>
              {confirmModal === 'DISCARD' && 'Are you sure you want to discard all changes? This action cannot be undone.'}
              {confirmModal === 'DRAFT' && 'Are you sure you want to save this requisition as a draft? You can submit it later.'}
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
              <Btn variant="outline" onClick={() => setConfirmModal(null)}>Cancel</Btn>
              <Btn onClick={() => executeAction(null, confirmModal)} disabled={saving} style={{ background: confirmModal === 'DISCARD' ? '#ef4444' : (confirmModal === 'RESUBMIT' ? '#16a34a' : '#eab308'), color: '#fff', border: 'none' }}>
                {saving ? 'Processing...' : 'Confirm'}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {showApproverModal && (
        <Modal title="Submit for Approval"
          onClose={saving ? () => { } : () => { pendingStatusRef.current = null; setShowApproverModal(false); }}>
          <div style={{ padding: 16 }}>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#475569' }}>
              This requisition will be sent for approval, starting with the step below.
            </p>
            <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
              First Approver{firstStepInfo?.step_name ? ` - ${firstStepInfo.step_name}` : ''}
            </label>
            {firstStepInfo?.approver_mode === 'specific_user' ? (
              <Select
                value={selectedApproverId}
                onChange={setSelectedApproverId}
                options={approvers.map((a) => ({ value: a.user_id || a.id, label: `${a.name} (${a.email})` }))}
              />
            ) : (
              <Select value="step_queue" onChange={() => {}} options={[{ value: 'step_queue', label: `🏢 ${firstStepInfo?.step_name || 'Approval Step'}` }]} />
            )}
          </div>
          <SlowRequestHint active={saving} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <Btn variant="outline" disabled={saving} onClick={() => { pendingStatusRef.current = null; setShowApproverModal(false); }}>Cancel</Btn>
            <Btn onClick={() => executeAction(selectedApproverId, 'SUBMITTED')} loading={saving} disabled={saving} style={{ background: '#16a34a', color: '#fff', border: 'none' }}>
              {saving ? 'Submitting…' : 'Submit PR'}
            </Btn>
          </div>
        </Modal>
      )}

      {showSuccessModal && (
        <Modal hideHeader width={420} onClose={handleSuccessModalClose}>
          <div style={{ padding: '24px 16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, boxShadow: '0 0 0 8px #f0fdf4' }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Requisition Submitted!</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
              Requisition <strong style={{ color: '#0b318bff' }}>{submittedPrRef}</strong> has been created and successfully submitted to {firstStepInfo?.step_name || 'the first approval step'} for processing.
            </p>
            <button
              onClick={handleSuccessModalClose}
              style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', color: '#fff', border: 'none', padding: '10px 32px', borderRadius: 20, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
            >
              Great, Thanks!
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function pillStyle(active) {
  return {
    padding: '10px 20px',
    borderRadius: 10,
    border: active ? '2px solid #0b318bff' : '1px solid black',
    background: active ? '#0b318bff' : '#fff',
    color: active ? '#fff' : '#0f172a',
    fontWeight: 800,
    fontSize: 13,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };
}

function Badge({ count }) {
  return (
    <span style={{ background: '#ef4444', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 800 }}>
      {count}
    </span>
  );
}
